import type { DocDetail } from "./types.js";

import { saveBlob } from "../../lib/download.js";
import { fetchDocFileBytes, fetchDocSourceFileBytes } from "./api.js";
import { buildDocPdf } from "./docPdf.js";

/**
 * Save a document to disk — the ONE "Download" behaviour for a document,
 * shared by the article reader and every row that offers Download (the
 * release documentation list, 2026-09-17), so a row and the reader never save
 * different files for the same document:
 *
 *   • an UPLOADED doc → the ORIGINAL file (the faithful `source_file` PDF,
 *     or the uploaded attachment of whatever type) — exactly what was put in;
 *   • an AUTHORED doc (a body with no upload) → a PDF built from the body
 *     (`buildDocPdf`), so a hand-written article still downloads as a document.
 *
 * Bytes come through the BFF's authenticated SAME-ORIGIN relay; nothing
 * cross-origin is fetched by the page. Saving goes through `saveBlob`.
 */
export async function downloadDocument(d: DocDetail): Promise<void> {
  const file = d.files[0];
  const sourceFile = d.source_file ?? null;

  if (sourceFile) {
    const { bytes, contentType } = await fetchDocSourceFileBytes(d.id);
    await saveBlob(
      sourceFile.filename || `${d.slug}.pdf`,
      new Blob([bytes], { type: contentType ?? "application/pdf" }),
    );
    return;
  }

  if (file) {
    const { bytes, contentType } = await fetchDocFileBytes(d.id);
    await saveBlob(
      file.filename || d.slug,
      new Blob([bytes], { type: contentType ?? file.content_type ?? "application/octet-stream" }),
    );
    return;
  }

  const { blob, filename } = await buildDocPdf(d, { fetchFileBytes: fetchDocFileBytes });
  await saveBlob(filename, blob);
}
